import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import './GA4ConnectionModal.css';

const GA4ConnectionModal = ({ isOpen, onClose, onConnect, site, initialProperties = null }) => {
  const { user } = useAuth();
  const [step, setStep] = useState(1); // 1: Auth, 2: Property Selection
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      // If we have initial properties from OAuth, skip auth step and go directly to selection
      if (initialProperties && initialProperties.length > 0) {
        setStep(2);
        setProperties(initialProperties);
      } else {
        setStep(1);
        setProperties([]);
      }
      setSelectedProperty(null);
      setError('');
    }
  }, [isOpen, initialProperties]);

  const handleGoogleAuth = async () => {
    setLoading(true);
    setError('');

    try {
      // Use server-side OAuth2 flow for refresh tokens
      const redirectUri = `${window.location.origin}/auth/google-oauth-callback`;
        
      // Include current user ID in state so we can connect analytics without changing login account
      const stateData = { 
        siteId: site.siteId,
        currentUserId: user?.id || null
      };
      
      const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${process.env.REACT_APP_GOOGLE_CLIENT_ID}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `response_type=code&` +
        `scope=${encodeURIComponent('https://www.googleapis.com/auth/analytics.readonly https://www.googleapis.com/auth/analytics.manage.users.readonly https://www.googleapis.com/auth/adsense.readonly https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile')}&` +
        `access_type=offline&` +
        `prompt=consent&` +
        `state=${encodeURIComponent(JSON.stringify(stateData))}`;

      // Redirect to Google OAuth2
      window.location.href = authUrl;
    } catch (error) {
      console.error('Google auth error:', error);
      setError('Authentication failed. Please try again.');
      setLoading(false);
    }
  };

  const handleConnect = () => {
    if (!selectedProperty) {
      setError('Please select a property');
      return;
    }

    onConnect(selectedProperty);
    onClose();
  };

  const handleBack = () => {
    setStep(1);
    setProperties([]);
    setSelectedProperty(null);
    setError('');
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content ga4-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {step === 1 ? 'Connect Google Analytics 4' : 'Select Property'}
          </h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="modal-body">
          {step === 1 && (
            <div className="auth-step">
              <div className="step-indicator">
                <div className="step active">1</div>
                <div className="step-line"></div>
                <div className="step">2</div>
              </div>

              <div className="auth-content">
                <div className="auth-icon">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2L2 7l10 5 10-5-10-5z" />
                    <path d="M2 17l10 5 10-5M2 12l10 5 10-5" />
                  </svg>
                </div>

                <h3>Authorize Google Analytics Access</h3>
                <p>
                  We need permission to access your Google Analytics 4 properties.
                  This allows us to fetch analytics data for <strong>{site.name}</strong>.
                </p>

                {error && <div className="error-message">{error}</div>}

                <button
                  className="btn-google-auth"
                  onClick={handleGoogleAuth}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="spinner-small"></div>
                      Connecting...
                    </>
                  ) : (
                    <>
                      <svg className="google-icon" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Connect with Google
                    </>
                  )}
                </button>

                <div className="auth-info">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path fillRule="evenodd" d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"/>
                  </svg>
                  <span>We only request read-only access to your analytics data</span>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="property-step">
              <div className="step-indicator">
                <div className="step completed">✓</div>
                <div className="step-line"></div>
                <div className="step active">2</div>
              </div>

              <div className="property-content">
                <h3>Select GA4 Property</h3>
                <p>Choose which Google Analytics 4 property to connect to <strong>{site.name}</strong></p>

                {error && <div className="error-message">{error}</div>}

                {properties.length === 0 ? (
                  <div className="empty-properties">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    <p>No GA4 properties found</p>
                  </div>
                ) : (
                  <div className="properties-list">
                    {properties.map((property) => (
                      <div
                        key={property.propertyId}
                        className={`property-item ${selectedProperty?.propertyId === property.propertyId ? 'selected' : ''}`}
                        onClick={() => setSelectedProperty(property)}
                      >
                        <div className="property-radio">
                          {selectedProperty?.propertyId === property.propertyId && (
                            <div className="radio-inner"></div>
                          )}
                        </div>
                        <div className="property-info">
                          <h4>{property.propertyName}</h4>
                          <p>{property.propertyId}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button className="btn-secondary" onClick={handleBack}>
                  Back
                </button>
                <button
                  className="btn-primary"
                  onClick={handleConnect}
                  disabled={!selectedProperty}
                >
                  Connect Property
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GA4ConnectionModal;

